import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import debounce from 'lodash/debounce';

import { gtmEventHandler } from '@common/utils/gtm';
import { useMediaQuery } from '@common/hooks';

import { updateRequestParams } from '@alumni_v2/stores/reviews/actions';
import { getPreSelectedFilters } from '@alumni_v2/utils/getPreSelectedFilters';
import {
  updateBackgroundColor,
  getSalaryThumbPosition,
} from '@alumni_v2/utils/filters';

import styles from './SalaryHikeFilter.module.scss';

const SalaryHikeFilter = ({
  salaryHikeFilter,
  setSalaryHikeFilter,
}) => {
  const dispatch = useDispatch();
  const { mobile } = useMediaQuery();

  const updateFiltersFn = useCallback((filters) => {
    const { preSelectedFiltersJson, sharedFilter } = getPreSelectedFilters();

    gtmEventHandler(
      'cta',
      'apply_filter',
      'click',
      'alumni_directory',
      {
        click_type: 'select_filter',
        click_section: 'filter_section_slider',
        click_text: 'Salary Hike',
        gtm_custom_attributes: {
          max_value: filters.growth[0].max,
          min_value: filters.growth[0].min,
        },
      },
    );
    dispatch(
      updateRequestParams(
        preSelectedFiltersJson,
        { ...sharedFilter, ...filters },
        null,
        false,
        true,
      ),
    );
  }, [dispatch]);

  const updateFilters = useCallback(
    debounce(updateFiltersFn, 1000), [updateFiltersFn],
  );

  const salarySliderFirst = useCallback((e) => {
    const newValue = parseInt(e.target.value, 10);
    const updatedFilter = newValue >= salaryHikeFilter?.max
      ? { min: salaryHikeFilter?.max, max: salaryHikeFilter?.max }
      : { min: newValue, max: salaryHikeFilter?.max };

    setSalaryHikeFilter(updatedFilter);

    updateFilters(
      {
        growth: [{ min: updatedFilter?.min, max: updatedFilter?.max }],
      },
      'slider',
    );
  }, [salaryHikeFilter, setSalaryHikeFilter, updateFilters]);

  const salarySliderSecond = useCallback((e) => {
    const newValue = parseInt(e.target.value, 10);
    const updatedFilter = newValue <= salaryHikeFilter?.min
      ? { min: salaryHikeFilter?.min, max: salaryHikeFilter?.min }
      : { min: salaryHikeFilter?.min, max: newValue };

    setSalaryHikeFilter(updatedFilter);

    updateFilters(
      {
        growth: [{ min: updatedFilter?.min, max: updatedFilter?.max }],
      },
      'slider',
    );
  }, [salaryHikeFilter, setSalaryHikeFilter, updateFilters]);

  const calculateThumbPosition = useCallback(
    (value, maxValue) => getSalaryThumbPosition(
      mobile, value, maxValue,
    ), [mobile],
  );

  useEffect(() => {
    updateBackgroundColor(salaryHikeFilter, 900, 'salary-slider-track');
  }, [salaryHikeFilter]);

  return (
    <div className={styles.salaryHikeSliderContainer}>
      <div className={styles.salaryHikeSliderTitle}>Salary Hike (%):</div>
      <div className={styles.salaryHikeSlider}>
        <div
          id="salary-slider-track"
          className={styles.salaryHikeSliderTrack}
        />
        <div
          className={styles.currentValueContainer}
          style={{
            top: mobile
              ? `${calculateThumbPosition(salaryHikeFilter?.min, 10)}rem` : '',
            left: !mobile
              ? `${calculateThumbPosition(salaryHikeFilter?.min, 10)}%` : '',
          }}
        >
          <span className={styles.currentValue}>
            {`${salaryHikeFilter?.min}%`}
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="900"
          step="10"
          value={salaryHikeFilter?.min}
          id="salaryHikeSlider-1"
          onInput={salarySliderFirst}
        />

        <div
          className={styles.currentValueContainer}
          style={{
            top: mobile
              ? `${calculateThumbPosition(salaryHikeFilter?.max, 10)}rem` : '',
            left: !mobile
              ? `${calculateThumbPosition(salaryHikeFilter?.max, 10)}%` : '',
          }}
        >
          <span className={styles.currentValue}>
            {`${salaryHikeFilter?.max}%`}
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="900"
          step="10"
          value={salaryHikeFilter?.max}
          id="salaryHikeSlider-2"
          onInput={salarySliderSecond}
        />
      </div>
    </div>
  );
};

export default SalaryHikeFilter;
